import {
  queriesInternalAccounts,
  queriesPayrolls,
  queriesWorkLogs
} from "@query";
import {
  toWSDateString,
  WSAlert,
  WSElement,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPill,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayrollExpand, WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { routeHelpersWorkLogPayerDetails } from "../../../WorkLogs/paths";
import { ApprovalWindowWidget } from "../../components/ApprovalWindowWidget";
import { PayrollRunDetailsCard } from "../../components/PayrollRunDetailsCard";
import { RecentHistoryWidget } from "../../components/RecentHistoryWidget";
import { FilterableTablePaystubs } from "../../components/TablePaystubs/FilterableTablePaystubs";
import { WalletBalanceCard } from "../../components/WalletBalanceCard";
import {
  useModalCancelPayroll,
  useModalDeletePayroll,
  useModalFinalizePayroll
} from "../../modals/PayrollRunActionModals";
import {
  routeHelperPayrollRunsDashboard,
  routeHelperPayrollRunsDetails,
  routeHelperPayrollRunsPaystub
} from "../../paths";
import { selectorPayrollRunApprovalSteps } from "../../selectors/selectorPayrollRunApprovalSteps";
import { selectorPayrollRunDetails } from "../../selectors/selectorPayrollRunDetails";
import { selectorPayrollRunsWalletBalance } from "../../selectors/selectorPayrollRunsWalletBalance";
import { PayrollRunsYear } from "../../types";

export const RoutePayrollRunsDetails: React.FC = () => {
  const goToDashboard = routeHelperPayrollRunsDashboard.useGoTo();
  const goToPaystub = routeHelperPayrollRunsPaystub.useGoTo();
  const goToWorkLogDetails = routeHelpersWorkLogPayerDetails.useGoTo();
  const modalFinalizePayroll = useModalFinalizePayroll();
  const modalCancelPayroll = useModalCancelPayroll();
  const modalDeletePayroll = useModalDeletePayroll();
  const {
    params: { year: yearParam, payrollRunId }
  } = routeHelperPayrollRunsDetails.useData();
  const year = yearParam as PayrollRunsYear;

  const queryPayroll = queriesPayrolls.getPayroll.useQuery({
    payrollId: payrollRunId,
    expand: [PayrollExpand.BREAKDOWN]
  });

  // Secondary queries stay OUTSIDE `WSQueries` (as unconditional hooks) — they
  // are `enabled`-gated on the primary query, so they never become `isSuccess`
  // on their own and would otherwise keep `WSQueries` stuck on the loader. Their
  // data is consumed with graceful fallbacks in the render prop below.
  //
  // Funding wallet + balance — same pattern as RoutePayrollRunsActivePayrolls.
  // Assumption: every run in this module is funded from the Wingspan wallet;
  // the v3 API exposes no per-run funding-source field to confirm this.
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const fundingAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );
  const queryBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: fundingAccount?.id ?? "" },
      { enabled: !!fundingAccount?.id }
    );

  // Unapproved work logs for this run's pay period. No server-side pay-period
  // filter exists (same caveat as the Paystub page), so we filter client-side.
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    { filter: { status: { anyOf: [WorkLogStatus.IN_REVIEW] } } },
    { enabled: !!queryPayroll.data?.payPeriodId }
  );

  return (
    <WSQueries queries={{ queryPayroll }}>
      {({ queryPayrollData }) => {
        const payrollRun = selectorPayrollRunDetails(queryPayrollData);
        const approvalSteps = selectorPayrollRunApprovalSteps(
          payrollRun.status
        );

        // Which run actions apply, from the mapped status. Finalize/cancel only
        // make sense before the run is finalized; delete only for an untouched
        // draft.
        const isBeforeFinalize =
          payrollRun.status === "opened" || payrollRun.status === "pending";
        const canFinalize = isBeforeFinalize;
        const canCancel = isBeforeFinalize;
        const canDelete = payrollRun.status === "opened";
        // The run still draws from the wallet balance until it's been paid or
        // failed/cancelled.
        const isPayable =
          payrollRun.status !== "completed" && payrollRun.status !== "failed";

        const walletBalance = selectorPayrollRunsWalletBalance({
          internalAccount: fundingAccount,
          balance: queryBalance.data
        });
        const hasInsufficientBalance =
          isPayable &&
          walletBalance.availableBalance < (payrollRun.liabilityAmount ?? 0);

        const unapprovedWorkLogs = (queryWorkLogs.flatData ?? []).filter(
          workLog => workLog.payPeriodId === queryPayrollData.payPeriodId
        );
        const handleBulkReviewWorkLogs = () => {
          const firstUnapproved = unapprovedWorkLogs[0];
          if (firstUnapproved?.id) {
            goToWorkLogDetails(
              { workLogId: firstUnapproved.id },
              { search: { bulkReview: "true" } }
            );
          }
        };

        const modalContext = {
          payrollId: payrollRunId,
          payrollNumber: payrollRun.payrollId
        };

        const handleFinalize = () => modalFinalizePayroll.open(modalContext);
        const handleCancel = () => modalCancelPayroll.open(modalContext);
        const handleDelete = async () => {
          // The run no longer exists after a delete, so leave the detail page.
          const deleted = await modalDeletePayroll.open(modalContext);
          if (deleted) {
            goToDashboard({ year });
          }
        };

        type ToolbarAction = NonNullable<
          React.ComponentProps<typeof WSPageToolbar>["actions"]
        >[number];
        const toolbarActions: ToolbarAction[] = [];
        if (canFinalize) {
          toolbarActions.push({
            label: "Finalize now",
            buttonKind: "Secondary",
            onClick: handleFinalize
          });
        }
        toolbarActions.push({ separator: true });
        if (canCancel) {
          toolbarActions.push({
            label: "Cancel payroll run",
            onClick: handleCancel
          });
        }
        if (canDelete) {
          toolbarActions.push({
            label: "Delete payroll run",
            icon: "trash",
            onClick: handleDelete
          });
        }
        toolbarActions.push({ label: "Download summary", icon: "download" });
        toolbarActions.push({
          label: "Manage payroll settings",
          icon: "settings"
        });

        return (
          <WSPage>
            <WSPageToolbar
              actions={toolbarActions}
              breadcrumbs={[
                {
                  label: "Payroll runs",
                  onClick: () => goToDashboard({ year })
                },
                { label: `Payroll run #${payrollRun.payrollId}` }
              ]}
              title={`Payroll run #${payrollRun.payrollId} · ${payrollRun.payPeriodLabel}`}
              titleAfter={
                payrollRun.isOffCycle ? (
                  <WSPill badge text="Off-cycle" theme="neutral" />
                ) : undefined
              }
            />

            {hasInsufficientBalance && (
              <WSAlert
                icon="alert"
                mt="L"
                theme="warning"
                title="Insufficient balance"
              >
                {`Your Wingspan wallet balance (${formatMoney(
                  walletBalance.availableBalance,
                  Currency.USD
                )}) is below this run's liability amount (${formatMoney(
                  payrollRun.liabilityAmount ?? 0,
                  Currency.USD
                )}). Top up your wallet before this run is finalized.`}
              </WSAlert>
            )}

            {payrollRun.worklogApprovalDueDate && (
              <WSAlert
                action={
                  unapprovedWorkLogs.length
                    ? {
                        text: "Bulk review worklogs",
                        onClick: handleBulkReviewWorkLogs
                      }
                    : undefined
                }
                icon="alert"
                mt="L"
                theme="warning"
                title="Ongoing worklog approvals."
              >
                {`${unapprovedWorkLogs.length} unapproved worklog${
                  unapprovedWorkLogs.length === 1 ? "" : "s"
                } for this pay period. Review and approve worklogs by ${toWSDateString(
                  payrollRun.worklogApprovalDueDate,
                  "monthDayYear"
                )}. You have ${payrollRun.worklogApprovalDaysLeft} days left. Any unapproved worklogs won't be included in this payroll run.`}
              </WSAlert>
            )}

            <WSGrid gutter="L" mt="XL">
              <WSGrid.Item span={{ s: "12", l: "8" }}>
                <WSElement>
                  <WSSectionToolbar title="Payroll run details" />
                  <WSElement mt="M">
                    <PayrollRunDetailsCard payrollRun={payrollRun} />
                  </WSElement>
                </WSElement>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", l: "4" }}>
                <WSList gap="L">
                  <ApprovalWindowWidget steps={approvalSteps} />
                  <WalletBalanceCard balance={walletBalance} />
                  <RecentHistoryWidget />
                </WSList>
              </WSGrid.Item>
            </WSGrid>

            <WSElement mt="2XL">
              <WSSectionToolbar title="Paystubs" />
              <WSElement mt="M">
                <FilterableTablePaystubs
                  payrollRunId={payrollRunId}
                  onRowOpen={paystubId =>
                    goToPaystub({ year, payrollRunId, paystubId })
                  }
                />
              </WSElement>
            </WSElement>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
